import { type JSX, useMemo, useState } from "react";
import { useQueries, type UseQueryResult } from "@tanstack/react-query";
import { ApiError } from "../../../api/client";
import { useApiClient } from "../../../api/context";
import { useWorldlineTree } from "../../../api/hooks/useWorldlineTree";
import { useScenario } from "../../../api/hooks/useScenario";
import { usePlotDrafts } from "../../../api/hooks/usePlotDrafts";
import { useForkWorldline } from "../../../api/hooks/useForkWorldline";
import { ErrorBanner } from "../../player/ErrorBanner";
import { mapProblemDetail } from "../../../errors/mapProblemDetail";
import type { UIError } from "../../../errors/types";
import type { components } from "../../../api/schema";
import { PalimpsestHeader } from "./PalimpsestHeader";
import { PalimpsestGrid } from "./PalimpsestGrid";
import { PalimpsestEndingColumn } from "./PalimpsestEndingColumn";
import { WorldInfoPanel } from "./WorldInfoPanel";
import { SceneDetailPanel } from "./SceneDetailPanel";

type Scene = components["schemas"]["Scene"];
type WorldlineNode = components["schemas"]["WorldlineNode"];
type Scenario = components["schemas"]["Scenario"];

export type PalimpsestViewProps = {
  scenarioId: string;
  /** 現在プレイ中の worldline id。null は「まだプレイ未開始」。 */
  currentWorldlineId: string | null;
  /** 閉じるボタン (Header) 押下時のコールバック。 */
  onClose: () => void;
  /** fork 成功時のコールバック。上位 (PlayerScreen) が新 worldline へ遷移する用。 */
  onForkSuccess?: (newWorldlineId: string) => void;
};

/**
 * useQueries で 各 worldline の scenes を並列 fetch するヘルパ。
 */
function useParallelScenes(
  worldlines: WorldlineNode[],
): Array<{ worldlineId: string; result: UseQueryResult<Scene[], ApiError> }> {
  const client = useApiClient();

  const results = useQueries({
    queries: worldlines.map((wl) => ({
      queryKey: ["worldlineScenes", wl.id] as const,
      staleTime: Infinity,
      queryFn: async (): Promise<Scene[]> => {
        const { data, error } = await client.GET("/worldlines/{worldlineId}/scenes", {
          params: { path: { worldlineId: wl.id } },
        });
        if (error !== undefined) {
          throw new ApiError(
            typeof (error as { status?: number }).status === "number"
              ? (error as { status: number }).status
              : 0,
            error as unknown as ApiError["problem"],
          );
        }
        return data ?? [];
      },
    })),
  });

  return worldlines.map((wl, i) => ({
    worldlineId: wl.id,
    result: results[i] as UseQueryResult<Scene[], ApiError>,
  }));
}

/**
 * worldline-palimpsest (Phase 3-1, Task 5.7) palimpsest 2D view 本体。
 *
 * hooks の統合 / loading / error 処理 / scene 詳細パネルの開閉 / fork 起動 を
 * 一元的に担当する。Modal 側 (PalimpsestModal) から 1 props (scenarioId +
 * currentWorldlineId) を受けて描画する。
 */
export function PalimpsestView({
  scenarioId,
  currentWorldlineId,
  onClose,
  onForkSuccess,
}: PalimpsestViewProps): JSX.Element {
  const scenarioQ = useScenario(scenarioId);
  const treeQ = useWorldlineTree(scenarioId);
  const plotDraftsQ = usePlotDrafts(scenarioId);
  const fork = useForkWorldline(scenarioId);

  const worldlines = useMemo(() => treeQ.data ?? [], [treeQ.data]);
  const parallelScenes = useParallelScenes(worldlines);

  // scenesByWorldline + endingEntries を 1 パスで組み立て (Req 4.x)
  const { scenesByWorldline, endingEntries } = useMemo(() => {
    const by: Record<string, Scene[]> = {};
    const endings: Array<{ worldline: WorldlineNode; finalSceneId: string | null }> = [];

    // 表示順: createdAt desc (最新が上段) = PalimpsestGrid と揃える
    const sortedDesc = [...worldlines].sort((a, b) =>
      b.createdAt.localeCompare(a.createdAt),
    );
    for (const wl of sortedDesc) {
      const entry = parallelScenes.find((p) => p.worldlineId === wl.id);
      const scenes = entry?.result.data ?? [];
      by[wl.id] = scenes;
      const finalScene = scenes[scenes.length - 1];
      endings.push({
        worldline: wl,
        finalSceneId: finalScene?.id ?? null,
      });
    }
    return { scenesByWorldline: by, endingEntries: endings };
  }, [worldlines, parallelScenes]);

  // 既分岐マップ (sceneId-choiceId → worldlineId) を SceneDetailPanel に渡す用
  const existingBranchesBySceneId = useMemo(() => {
    const m = new Map<string, Record<string, string>>();
    for (const wl of worldlines) {
      if (
        wl.branchFromSceneId !== null &&
        wl.branchFromSceneId !== undefined &&
        wl.branchFromChoiceId !== null &&
        wl.branchFromChoiceId !== undefined
      ) {
        const sceneId = wl.branchFromSceneId;
        const existing = m.get(sceneId) ?? {};
        existing[wl.branchFromChoiceId] = wl.id;
        m.set(sceneId, existing);
      }
    }
    return m;
  }, [worldlines]);

  // scene 詳細 drawer の state
  const [openDetail, setOpenDetail] = useState<{
    scene: Scene;
    worldline: WorldlineNode;
  } | null>(null);

  // world info collapse state (header の toggle と連動)
  const [worldInfoOpen, setWorldInfoOpen] = useState(false);

  // 最新の未踏 "?" 提案エラー (toast 相当の軽量表示)
  const [previewError, setPreviewError] = useState<UIError | null>(null);

  // loading
  const isLoading =
    scenarioQ.isLoading || treeQ.isLoading || plotDraftsQ.isLoading;
  // 致命エラー: tree / scenario が失敗した場合のみブロッキング表示
  const fatalError: UIError | null = (() => {
    if (treeQ.isError && treeQ.error !== null) return mapProblemDetail(treeQ.error);
    if (scenarioQ.isError && scenarioQ.error !== null) return mapProblemDetail(scenarioQ.error);
    return null;
  })();

  const handleForkFromChoice = (
    worldlineId: string,
    sceneId: string,
    choiceId: string,
  ): void => {
    fork.mutate(
      {
        worldlineId,
        branchFromSceneId: sceneId,
        branchFromChoiceId: choiceId,
      },
      {
        onSuccess: (newWorldline) => {
          setOpenDetail(null);
          onForkSuccess?.(newWorldline.id);
        },
        onError: (err) => {
          // fork 失敗は drawer 内に ErrorBanner で表示する設計だが、
          // MVP としては軽量に UIError を previewError と同じ slot に流す
          setPreviewError(mapProblemDetail(err));
        },
      },
    );
  };

  const latestPlotDraft = plotDraftsQ.data?.[0] ?? null;
  const scenario: Scenario | undefined = scenarioQ.data;

  return (
    <div
      data-testid="palimpsest-view"
      className="flex flex-col gap-2 max-h-full"
      aria-busy={isLoading}
    >
      <PalimpsestHeader
        {...(scenario?.title !== undefined ? { title: scenario.title } : {})}
        onClose={onClose}
        onToggleWorldInfo={() => setWorldInfoOpen((v) => !v)}
        worldInfoOpen={worldInfoOpen}
      />

      {worldInfoOpen && (
        <WorldInfoPanel
          worldview={
            scenario === undefined
              ? null
              : ((scenario as unknown as { worldview?: unknown }).worldview as
                  | Record<string, unknown>
                  | null
                  | undefined) ?? null
          }
          defaultOpen
        />
      )}

      {fatalError !== null && (
        <ErrorBanner
          error={fatalError}
          onRetry={() => {
            void treeQ.refetch();
            void scenarioQ.refetch();
          }}
        />
      )}

      {previewError !== null && (
        <div
          data-testid="palimpsest-preview-error-toast"
          role="status"
          className="text-xs px-3 py-2 rounded bg-slate-800/80 border border-slate-700 text-slate-200"
        >
          {previewError.body}
          <button
            type="button"
            onClick={() => setPreviewError(null)}
            className="ml-2 text-slate-400 hover:text-slate-100"
            aria-label="toast を閉じる"
          >
            ✕
          </button>
        </div>
      )}

      {isLoading && fatalError === null && (
        <p
          data-testid="palimpsest-view-loading"
          className="text-sm text-slate-400 px-3 py-2"
        >
          読み込み中…
        </p>
      )}

      {!isLoading && fatalError === null && (
        <div className="flex gap-2 overflow-auto">
          <div className="flex-1 min-w-0">
            <PalimpsestGrid
              worldlines={worldlines}
              scenesByWorldline={scenesByWorldline}
              currentWorldlineId={currentWorldlineId}
              plotDraft={latestPlotDraft}
              onTapScene={(scene, worldline) =>
                setOpenDetail({ scene, worldline })
              }
              onPreviewError={(err) => setPreviewError(err)}
            />
          </div>
          <PalimpsestEndingColumn entries={endingEntries} />
        </div>
      )}

      {openDetail !== null && (
        <div
          data-testid="palimpsest-scene-detail-drawer"
          className="mt-2"
          role="region"
          aria-label="scene 詳細パネル"
        >
          <SceneDetailPanel
            scene={openDetail.scene}
            worldline={openDetail.worldline}
            {...(scenario !== undefined
              ? {
                  characters:
                    (scenario as unknown as { characters?: unknown })
                      .characters as never,
                }
              : {})}
            existingBranches={
              existingBranchesBySceneId.get(openDetail.scene.id) ?? {}
            }
            onForkFromChoice={handleForkFromChoice}
            onSwitchWorldline={(worldlineId) => {
              setOpenDetail(null);
              onForkSuccess?.(worldlineId);
            }}
          />
          <button
            type="button"
            data-testid="palimpsest-scene-detail-close"
            onClick={() => setOpenDetail(null)}
            className="mt-1 text-xs text-slate-400 hover:text-slate-100"
          >
            詳細を閉じる
          </button>
        </div>
      )}
    </div>
  );
}
